<script setup lang="ts">
import { inject, onMounted, onUnmounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { Button } from '../../../../misc'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

interface Emits {
  (e: 'next'): void
  (e: 'skip'): void
}

const emit = defineEmits<Emits>()
const { t } = useI18n()
const contentReady = inject<import('vue').Ref<boolean>>('onboarding-content-ready', ref(true))

// 检测平台（延迟到 onMounted 避免 SSR 问题）
const isIOS = ref(false)
const isStandalone = ref(false)

// beforeinstallprompt 捕获（Android/桌面 Chrome/Edge）
const deferredPrompt = ref<BeforeInstallPromptEvent | null>(null)
const canInstall = ref(false)
const isInstalling = ref(false)
const installed = ref(false)

// iOS 步骤列表（提取为常量避免每次渲染重新创建）
const iosSteps = [
  { icon: 'i-solar:share-bold-duotone', key: 'iosStep1' },
  { icon: 'i-solar:add-square-bold-duotone', key: 'iosStep2' },
  { icon: 'i-solar:check-square-bold-duotone', key: 'iosStep3' },
] as const

let skipTimer: ReturnType<typeof setTimeout> | null = null

function onBeforeInstallPrompt(e: Event) {
  e.preventDefault()
  deferredPrompt.value = e as BeforeInstallPromptEvent
  canInstall.value = true
  // 事件已到，取消跳过计时器
  if (skipTimer) {
    clearTimeout(skipTimer)
    skipTimer = null
  }
}

onMounted(() => {
  // 在 mounted 中安全访问 window/navigator
  isIOS.value = (
    /iPad|iPhone|iPod/.test(navigator.userAgent)
    || (navigator.maxTouchPoints > 1 && /Mac/.test(navigator.userAgent))
  ) && !(window as unknown as Record<string, unknown>).MSStream

  isStandalone.value = window.matchMedia('(display-mode: standalone)').matches
    || (navigator as unknown as Record<string, unknown>).standalone === true

  // 已安装则直接跳过
  if (isStandalone.value) {
    emit('skip')
    return
  }

  window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt)

  // 既不是 iOS 也没有 beforeinstallprompt 支持，等待 1500ms 再判断
  // （beforeinstallprompt 可能在 mounted 后才触发，低端设备需要更多时间）
  skipTimer = setTimeout(() => {
    if (!isIOS.value && !canInstall.value) {
      emit('skip')
    }
  }, 1500)
})

onUnmounted(() => {
  window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt)
  if (skipTimer) {
    clearTimeout(skipTimer)
    skipTimer = null
  }
})

async function handleInstall() {
  if (!deferredPrompt.value)
    return
  isInstalling.value = true
  try {
    deferredPrompt.value.prompt()
    const { outcome } = await deferredPrompt.value.userChoice
    if (outcome === 'accepted') {
      installed.value = true
      setTimeout(() => emit('next'), 1000)
    }
  }
  finally {
    isInstalling.value = false
    deferredPrompt.value = null
    canInstall.value = false
  }
}
</script>

<template>
  <div class="flex flex-1 flex-col items-center justify-between gap-6 py-4">
    <template v-if="contentReady">
      <!-- 图标 + 标题 -->
      <div class="flex flex-col items-center gap-3 text-center">
        <div
          v-motion
          :initial="{ opacity: 0, scale: 0.5 }"
          :enter="{ opacity: 1, scale: 1 }"
          :duration="400"
          class="h-20 w-20 flex items-center justify-center rounded-2xl bg-primary-100 dark:bg-primary-900/30"
        >
          <div class="i-solar:smartphone-2-bold-duotone h-10 w-10 text-primary-500" />
        </div>
        <div
          v-motion
          :initial="{ opacity: 0, y: 10 }"
          :enter="{ opacity: 1, y: 0 }"
          :duration="380"
          :delay="100"
        >
          <h2 class="text-2xl text-neutral-800 font-semibold dark:text-neutral-100">
            {{ t('settings.dialogs.onboarding.installPwa.title') }}
          </h2>
          <p class="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
            {{ t('settings.dialogs.onboarding.installPwa.description') }}
          </p>
        </div>
      </div>

      <!-- iOS 手动引导 -->
      <div
        v-if="isIOS"
        v-motion
        :initial="{ opacity: 0, y: 10 }"
        :enter="{ opacity: 1, y: 0 }"
        :duration="400"
        :delay="200"
        class="w-full space-y-3"
      >
        <div
          v-for="(step, i) in iosSteps"
          :key="i"
          class="flex items-center gap-3 rounded-xl bg-neutral-100 px-4 py-3 dark:bg-neutral-800"
        >
          <div class="h-8 w-8 flex flex-shrink-0 items-center justify-center rounded-full bg-primary-500 text-sm text-white font-bold">
            {{ i + 1 }}
          </div>
          <div :class="step.icon" class="h-5 w-5 flex-shrink-0 text-primary-500" />
          <span class="text-sm text-neutral-700 dark:text-neutral-300">{{ t(`settings.dialogs.onboarding.installPwa.${step.key}`) }}</span>
        </div>
      </div>

      <!-- Android/桌面：安装成功提示 -->
      <div
        v-else-if="installed"
        v-motion
        :initial="{ opacity: 0, scale: 0.8 }"
        :enter="{ opacity: 1, scale: 1 }"
        :duration="400"
        class="flex flex-col items-center gap-2 text-center"
      >
        <div class="i-solar:check-circle-bold-duotone h-12 w-12 text-green-500" />
        <p class="text-sm text-neutral-600 dark:text-neutral-400">
          {{ t('settings.dialogs.onboarding.installPwa.installedHint') }}
        </p>
      </div>

      <!-- 底部按钮 -->
      <div class="w-full space-y-2">
        <!-- iOS：已添加 + 跳过 -->
        <template v-if="isIOS">
          <Button
            :label="t('settings.dialogs.onboarding.installPwa.iosConfirm')"
            @click="emit('next')"
          />
          <button
            class="w-full py-2 text-sm text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300"
            @click="emit('skip')"
          >
            {{ t('settings.dialogs.onboarding.installPwa.skip') }}
          </button>
        </template>

        <!-- Android/桌面：一键安装 + 跳过 -->
        <template v-else-if="canInstall && !installed">
          <Button
            :label="t('settings.dialogs.onboarding.installPwa.installButton')"
            :loading="isInstalling"
            @click="handleInstall"
          />
          <button
            class="w-full py-2 text-sm text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300"
            @click="emit('skip')"
          >
            {{ t('settings.dialogs.onboarding.installPwa.skip') }}
          </button>
        </template>
      </div>
    </template>
  </div>
</template>
